import { getBuyOnceAsSellingPlanGroup } from '../../models/buy-once'
import { buildElement } from '../../utils/element'
import { widgetState } from '../../store/customizations/index'
import { buildTemplate, displaySelectedElement } from './widgets/template-builder'
import { radioTemplate } from '../templates/radio-template'
import { buildPriceLabel } from './widgets/price-label'
import { buildSelection } from './widgets/selection-builder'
import { selectionFactory } from './widgets/selection-factory'
import { Template } from '../../models/template'
import { SellingPlanGroup } from '../../models/selling-plan-group'
import { setSellingPlanChange } from '../../store/selling-plans/actions'
import { getProduct, getProductPrice } from '../../store/product/getters'
import { getSellingPlanGroups } from '../../store/selling-plans/getters'
import { appendElementsInOrder, getSelectedGroupIndex, getSellingPlansMainElement, getSellingPlansMainElementWrapper } from './layout'
import { PlanSelectionLayout } from '../../models/layout'
import { getLowestDiscountByGroupForVariant } from '../../models/selling-plan-allocation'
import { getDiscountLabel } from './widgets/discount'
import { SellingPlan } from '../../models/selling-plan'
import { sortStrategy } from '../../services/sort/selling-plans'

export class RadioButtons implements PlanSelectionLayout {
  render (): HTMLElement {
    const sellingPlanGroupElements = getSellingPlanGroups().map((group, index) => {
      const isSelected = getSelectedGroupIndex() === (1 + index)
      return this.buildSellingPlanGroupElement(group, isSelected, true)
    })

    const mainElement = getSellingPlansMainElement()
    appendElementsInOrder(mainElement, sellingPlanGroupElements, this.buildBuyOnce())
    this.expandGroupOnAutoSelectedIndex()

    const mainElementWrapper = getSellingPlansMainElementWrapper()
    mainElementWrapper.appendChild(mainElement)
    mainElementWrapper.appendChild(this.styles())

    return mainElementWrapper
  }

  private buildSellingPlanGroupElement (group: SellingPlanGroup, checked: boolean, isSubscription: boolean): HTMLElement {
    const container = buildElement('div', `yotpo-radio-container ${checked ? 'yotpo-selected' : ''}`, {
      'data-seling-plan-group-id': group.id
    })
    const radioElementsWrapper = buildElement('div', 'yotpo-radio-button-text-wrapper', {
      'data-seling-plan-group-id': group.id
    })

    const template = widgetState.customizations.selectionType.split('-')[2]

    const groupsTemplate = buildTemplate({
      id: group.id,
      value: group.sellingPlans[0] ? group.sellingPlans[0].id : '',
      text: group.name,
      checked,
      classString: 'yotpo-radio-buttons',
      inputAttributes: {
        data: group.sellingPlans[0] ? group.sellingPlans[0].name : '',
        name: 'selling_plan'
      }
    },
    radioTemplate()
    )

    const priceLabel = buildPriceLabel(group, isSubscription, checked)

    const frequencyTemplate = selectionFactory(template as Template)
    const sortingStrategy = sortStrategy(group.id)
    const frequencyTemplateElement = buildSelection({
      values: group.sellingPlans.sort(sortingStrategy).map((x, index) => {
        const label = this.getSellingPlanLabel(x, group.id)
        return {
          id: x.id,
          label: label,
          value: x.id.toString(),
          checked: index === 0
        }
      }),
      attributes: {},
      classString: 'yotpo-select-wrapper',
      id: group.id,
      groupId: group.id
    },
    frequencyTemplate
    )

    radioElementsWrapper.appendChild(groupsTemplate)
    radioElementsWrapper.appendChild(priceLabel)
    container.appendChild(radioElementsWrapper)

    this.setElementClassForChangeEvent(group, container, frequencyTemplateElement, isSubscription)
    this.registerToElementChangeEvent(group, container, frequencyTemplateElement, isSubscription)

    return container
  }

  private registerToElementChangeEvent (group: SellingPlanGroup, element: HTMLElement, container: HTMLElement, isSubscription: boolean) {
    element.addEventListener('change', (event: Event) => {
      const selectedSellingPlanId = (event.target as HTMLInputElement).value
      const price = getProductPrice()
      const isDiscountAvailable = price !== getLowestDiscountByGroupForVariant(group.id) && price > 0
      setSellingPlanChange(group.id, selectedSellingPlanId, isSubscription, getProduct().id.toString(), isDiscountAvailable)
      if (widgetState.customizations.selectionType === 'radio-and-dropdown') {
        const dropdownEl = document.querySelector('.yotpo-widget-subscriptions-add-to-cart .yotpo-frequency-options')
        if (dropdownEl) {
          (dropdownEl as HTMLElement).blur()
        }
        if (widgetState.customizations.styles.selectedRadio.openByDefault) {
          const parentInput = document.querySelector(`.yotpo-radio-button-text-wrapper[data-seling-plan-group-id="${group.id}"] .yotpo-radio-label-input`)
          if (parentInput) {
            (parentInput as HTMLInputElement).checked = true
          }
        }
      }
    })
  }

  private styles (): any {
    const radioStyle = buildElement('style', 'yotpo-radio-style', {})
    radioStyle.innerHTML = `
    .yotpo-widget-subscriptions-add-to-cart {
      -webkit-appearance: none;
      -moz-appearance: none;
      background-repeat: no-repeat;
      background-position-x: 96%;
      background-position-y: 50%;
      background-size: 10%;
      border-radius: 3px;
      margin-right: 2rem;
      padding: 10px 20px 10px 10px;
      display: contents;
      border: 1px solid transparent;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-selling-plans-wrapper {
      display: block;
      margin: 20px 0;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-selling-plans-wrapper.yotpo-admin-preview {
      text-align: center;
      min-height: auto;
      display: block;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-plans-and-policy-wrapper {
      max-width: 500px;
    }

    .yotpo-widget-subscriptions-add-to-cart.yotpo-admin-preview .yotpo-plans-and-policy-wrapper {
      min-height:300px;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-selling-plans {
      width: 100%;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-is-preview .yotpo-selling-plans {
      max-width: 400px;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-admin-preview .yotpo-selling-plans {
      text-align: left;
      display: inline-block;
      min-width: 150px;
      width: 100%;
      max-width: 400px;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container {
      background-color: ${widgetState.customizations.styles.notSelectedRadio.backgroundColor};
      border-radius: 8px;
      padding: 10px 15px;
      margin: 5px 0;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container {
      border: 1px solid transparent;
      border-radius: 3px;
      min-width: 230px;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container.yotpo-selected {
      border-style: ${widgetState.customizations.styles.selectedRadio.borderStyle};
      border-width: ${widgetState.customizations.styles.selectedRadio.borderSize};
      border-color: ${widgetState.customizations.styles.selectedRadio.borderColor};
      background-color: ${widgetState.customizations.styles.selectedRadio.backgroundColor};
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-button-text-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-subscription-price-label-wrapper {
      display: flex;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-subscription-price-label-container {
      display: flex;
      justify-content: left;
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-subscription-label-prefix {
      font-style: normal;
      font-family: ${widgetState.customizations.styles.fontFamily.name};
      font-size: ${widgetState.customizations.styles.fontSize.sellingPlans.name};
      font-weight: ${widgetState.customizations.styles.fontFamily.weight || 600};
      color: ${widgetState.customizations.styles.colors.text.primary};
      margin-right: 10px;
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-selected .yotpo-subscription-label-prefix {
      color: ${widgetState.customizations.tiles.fontColor};
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-subscription-label-price {
      font-style: normal;
      font-family: ${widgetState.customizations.styles.fontFamily.name};
      font-size: ${widgetState.customizations.styles.fontSize.sellingPlans.name};
      font-weight: ${widgetState.customizations.styles.fontFamily.weight || 600};
      color: ${widgetState.customizations.styles.colors.text.primary};
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-selected .yotpo-subscription-label-price {
      color: ${widgetState.customizations.tiles.fontColor};
    }

    .yotpo-widget-subscriptions-add-to-cart .yotpo-label-for-select-type {
      display: inline-block;
      margin-right: 5px;
    }`
    return radioStyle
  }

  private buildBuyOnce () {
    const buyOnceGroup = getBuyOnceAsSellingPlanGroup()
    const isSelected = widgetState.customizations.autoPickOptionIndex === 0
    return this.buildSellingPlanGroupElement(buyOnceGroup, isSelected, false)
  }

  private setElementClassForChangeEvent (group: SellingPlanGroup, element: HTMLElement, container: HTMLElement, isSubscription: boolean) {
    if (group.sellingPlans.length > 0) {
      element.appendChild(container)
    }

    const klass = isSubscription ? 'yotpo-radio-subscription-container' : 'yotpo-radio-buy-once-container'
    element.classList.add(klass)
  }

  private expandGroupOnAutoSelectedIndex () {
    const selectedIndex = getSelectedGroupIndex()
    if (selectedIndex >= 1) {
      const groupByIndex = getSellingPlanGroups()[selectedIndex - 1]
      setTimeout(displaySelectedElement(groupByIndex.id), 0)
    }
  }

  private getSellingPlanLabel (sellingPlan: SellingPlan, groupId: string): string {
    if (!widgetState.customizations.sellingPlans.showDiscount) {
      return sellingPlan.name
    }
    const discountLabel = getDiscountLabel(sellingPlan.priceAdjustments, groupId)
    const label = `${sellingPlan.name} ${discountLabel}`
    return label.trim()
  }
}
